import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useCustomer } from './CustomerContext';

const API = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';
const LOCAL_KEY = (subdomain: string) => `dilzee_wishlist_${subdomain}`;

interface WishlistContextType {
  wishlist: string[];           // array of product_ids
  isWishlisted: (productId: string) => boolean;
  toggle: (productId: string) => void;  // optimistic UI
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType | null>(null);

export function WishlistProvider({ children, subdomain }: { children: ReactNode; subdomain: string }) {
  const { token, isLoggedIn } = useCustomer();
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Load wishlist on mount and when auth state changes
  useEffect(() => {
    if (isLoggedIn && token) {
      // Logged in: fetch from server, then merge any localStorage items
      setLoading(true);
      fetch(`${API}/public/${subdomain}/wishlist`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(async data => {
          const serverIds = (data.items || []).map((i: any) => i.product_id);

          // Merge localStorage wishlist into server
          const localRaw = localStorage.getItem(LOCAL_KEY(subdomain));
          const localIds: string[] = localRaw ? JSON.parse(localRaw) : [];

          if (localIds.length > 0) {
            // Sync local to server
            fetch(`${API}/public/${subdomain}/wishlist/sync`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ product_ids: localIds }),
            }).catch(() => {});
            localStorage.removeItem(LOCAL_KEY(subdomain));
          }

          // Merge both lists for immediate display
          const merged = Array.from(new Set([...serverIds, ...localIds]));
          setWishlist(merged);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      // Not logged in: read from localStorage
      try {
        const stored = localStorage.getItem(LOCAL_KEY(subdomain));
        setWishlist(stored ? JSON.parse(stored) : []);
      } catch {
        setWishlist([]);
      }
    }
  }, [isLoggedIn, token, subdomain]);

  const isWishlisted = useCallback((productId: string) => wishlist.includes(productId), [wishlist]);

  const toggle = useCallback((productId: string) => {
    const currentlyWishlisted = wishlist.includes(productId);

    // OPTIMISTIC UI: update state immediately before API call
    setWishlist(prev =>
      currentlyWishlisted
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );

    if (isLoggedIn && token) {
      // Logged in: sync to server in background
      fetch(`${API}/public/${subdomain}/wishlist/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ product_id: productId }),
      }).catch(() => {
        // Revert optimistic update on failure
        setWishlist(prev =>
          currentlyWishlisted
            ? [...prev, productId]
            : prev.filter(id => id !== productId)
        );
      });
    } else {
      // Not logged in: persist to localStorage
      setWishlist(prev => {
        const updated = currentlyWishlisted
          ? prev.filter(id => id !== productId)
          : [...prev, productId];
        try { localStorage.setItem(LOCAL_KEY(subdomain), JSON.stringify(updated)); } catch {}
        return updated;
      });
    }
  }, [wishlist, isLoggedIn, token, subdomain]);

  return (
    <WishlistContext.Provider value={{ wishlist, isWishlisted, toggle, loading }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used inside WishlistProvider');
  return ctx;
}
